import { Authorized } from '@common/components/trimming/Authorized';
import { action, makeObservable, observable } from 'mobx';
import React from 'react';
import { Params, RouteMatch, RouteObject } from 'react-router';
import uniqid from 'uniqid';
export interface IBuildRouteReturn {
    route: IExtRouteObject
}

export interface IExtRouteMatch extends RouteMatch {
}


export class ExtRouteMatch implements IExtRouteMatch {
    pathnameBase: string;
    params: Params<string>;
    pathname: string;
    route: ExtRouteObject;

    constructor(route: ExtRouteObject, pathname: string, params: Params<string>, pathnameBase: string) {
        this.params = params;
        this.route = route;
        this.pathname = pathname;
        this.pathnameBase = pathnameBase;
    }
}

export interface IExtRouteObjectOptions {
    hasAccess?: boolean;
    skipBreadCrumb?: boolean;
    children?: IExtRouteObject[];
    redirectTo?: string;
    setRouteReference?: (route: IExtRouteObject) => void;
}
export interface IExtRouteObjectInit {
    path?: string,
    title?: string,
    pageTitle?: string,
    redirectTo?: string,
    caseSensitive?: boolean,
    element: React.ReactNode
}

export interface IExtRouteObject extends RouteObject {
    uniqueId: string;
    title?: string;
    pageTitle?: string;
    hasAccess?: boolean;
    skipBreadCrumb?: boolean
    setTitle: (newTitle: string) => void;
}

/**
 * This class extends react routers RouteObject interface and allows us to extend what react router will track in it's state during route management for a route
 * It will track an array of these objects where we can have and retrieve data about a route on a per parent/child hierarchy.  We can store anything we want to be associated to routes in this class.
 */
export class ExtRouteObject implements IExtRouteObject {
    private internalElement!: React.ReactNode;
    uniqueId: string;
    @observable path?: string;
    @observable title?: string;
    @observable pageTitle?: string;
    @observable caseSensitive?: boolean = false;
    @observable children?: RouteObject[] | undefined;
    @observable hasAccess?: boolean;
    @observable skipBreadCrumb?: boolean;
    setRouteReference?: (route: ExtRouteObject) => void;

    @action.bound setTitle(newTitle: string) {
        this.title = newTitle;
    }

    constructor(init: IExtRouteObjectInit, options?: Partial<IExtRouteObjectOptions>) {
        makeObservable(this, undefined, { autoBind: true });

        this.uniqueId = uniqid(); //useful for key fields on elements
        this.path = init.path;
        this.title = init.title;
        this.pageTitle = init.pageTitle;
        this.caseSensitive = init.caseSensitive;
        this.internalElement = init.element;
        //options
        this.skipBreadCrumb = options?.skipBreadCrumb;
        this.children = options?.children ? options.children : [];
        this.hasAccess = options?.hasAccess;
        this.setRouteReference = options?.setRouteReference;
        this.init();
    }

    //This exists so that we can do some post constructor wiring up to external things that want to update the route.  I.e. so we can pass a reference to the route up to a store so the store can manipulat the route title etc.
    private init() {
        if (this.setRouteReference) {
            this.setRouteReference(this);
        }
    }

    /* Automatically handle authorized routes here, if hasAccess is set we wrap the route element in an Authorized automatically here and let it handle access trimming with a redirect to error page */
    get element() {
        if (this.hasAccess !== undefined) {
            return (
                <Authorized if={this.hasAccess} deniedMessage="You are not authorized to view this page.">
                    {
                        this.internalElement
                    }
                </Authorized>
            )
        }
        return this.internalElement;
    }
}
